/**
 * 订单批量导出的共享常量与纯函数。
 *
 * 放在 utils 而不是各组件内部，是因为「提交入口（列表页工具条）」「等待弹窗」「导出记录页」
 * 三处都要用同一套上限、权限码与错误文案映射，散开写必然会出现三者不一致却没人发现的情况。
 */

/**
 * 导出按钮与导出记录页共用的权限码。
 *
 * 带冒号，<b>无法由 AuthGuard 从路径反推</b>，所以 /order-export-records 必须注册在 routeMap 之外
 * 并在 AuthGuard 里显式判断，见 App.tsx 与 AuthGuard.tsx 的对应注释。
 */
export const EXPORT_PERMISSION = 'portal:order-query:export';

/**
 * 导出记录页路径。
 *
 * 取单段路径，与 /order-query、/order-cancel、/bill-list 一致；现有 routeMap 里没有任何多段路径。
 */
export const EXPORT_RECORDS_PATH = '/order-export-records';

/**
 * 单次导出的条数上限，与服务端 customer.portal.order-export.max-records 对齐。
 *
 * 前端持有它只为省一次「注定被拒」的往返（勾选 0 条且命中总数超限时直接提示），
 * <b>不是校验的唯一防线</b>——服务端提交阶段仍会 probe 命中总数并独立校验。
 * 服务端调大上限后这里漏改，症状只是前端多拦了一些本可通过的导出，不会放过超限请求。
 */
export const MAX_EXPORT_RECORDS = 10000;

/** 后端下发 messageKey 的命名空间；本地兜底映射也复用同一批 key，避免两套文案 */
const ERROR_KEY_PREFIX = 'errors.business.customer.orderExport';

/**
 * errorCode → 文案 key 的本地兜底映射。
 *
 * 覆盖提交（前 4 个）与下载（后 5 个）两组。SIGN_FAILED 对应的 key 叫 downloadUrlFailed
 * 而非 signFailed——key 由后端 MessageKey 枚举定义，这里跟随，不另起名字。
 */
const ERROR_CODE_KEYS: Readonly<Record<string, string>> = {
    EXCEEDS_LIMIT: `${ERROR_KEY_PREFIX}.exceedsLimit`,
    TRACKING_NUMBER_LENGTH_EXCEED_LIMIT: `${ERROR_KEY_PREFIX}.trackingNumberLengthExceedsLimit`,
    TOO_MANY_IN_PROGRESS: `${ERROR_KEY_PREFIX}.tooManyInProgress`,
    SYSTEM_BUSY: `${ERROR_KEY_PREFIX}.systemBusy`,
    TASK_NOT_FOUND: `${ERROR_KEY_PREFIX}.taskNotFound`,
    TASK_NOT_READY: `${ERROR_KEY_PREFIX}.taskNotReady`,
    TASK_FAILED: `${ERROR_KEY_PREFIX}.taskFailed`,
    FILE_MISSING: `${ERROR_KEY_PREFIX}.fileMissing`,
    SIGN_FAILED: `${ERROR_KEY_PREFIX}.downloadUrlFailed`,
};

/** 既没有可翻译的 messageKey、errorCode 也不认识时的通用兜底 */
export const GENERIC_EXPORT_ERROR_KEY = 'orderExport.error.downloadUnavailable';

/**
 * 挑一个「一定翻得出来」的文案 key。
 *
 * <p>优先用后端下发的 messageKey（沿用「后端下发 key + 前端翻译」的既有约定），但必须先确认本地
 * 确实有这条翻译：i18next 找不到 key 时会把 key 原样回显，客户会看到
 * "errors.business.customer.orderExport.xxx" 这种串。后端加了新 messageKey 而门户没跟进翻译
 * 是跨仓改动下的常态，本地 translation.test.ts 拦不住它，所以这里必须自己兜。
 *
 * <p>兜底顺序：后端 messageKey（本地有译文）→ errorCode 的本地映射（本地有译文）→ 通用文案。
 *
 * @param errorCode 后端返回的错误码
 * @param messageKey 后端下发的文案键
 * @param exists 判断本地是否存在该翻译，传 i18n.exists；抽成入参只为可测
 */
export const resolveExportErrorKey = (
    errorCode: string | null | undefined,
    messageKey: string | null | undefined,
    exists: (key: string) => boolean
): string => {
    if (messageKey && exists(messageKey)) return messageKey;
    const mapped = errorCode ? ERROR_CODE_KEYS[errorCode] : undefined;
    if (mapped && exists(mapped)) return mapped;
    return GENERIC_EXPORT_ERROR_KEY;
};

/** Excel 状态列的语言白名单，与服务端 uiLanguage 参数一致；白名单外一律降级 en */
const UI_LANGUAGES = ['en', 'zh', 'es'];

/**
 * 把 i18next 的当前语言归一为服务端接受的 uiLanguage。
 *
 * 浏览器语言检测会给出 "zh-CN" / "es-MX" 这类带地区的标签，服务端只认 en/zh/es，
 * 原样下发会被降级成 en——界面是中文、导出的 Excel 状态列却是英文，且没有任何报错。
 */
export const resolveUiLanguage = (language: string | undefined | null): string => {
    const primary = (language || '').split('-')[0].toLowerCase();
    return UI_LANGUAGES.includes(primary) ? primary : 'en';
};

/**
 * 文件字节数格式化为 KB/MB。
 *
 * 后端以字符串下发（避免超出 JS 安全整数范围），空值、非数字、负数一律返回 '-'——
 * 与列表其它空值列的占位一致，不渲染 "NaN B"。
 */
export const formatFileSize = (bytes: string | null | undefined): string => {
    if (bytes === null || bytes === undefined || bytes === '') return '-';
    const value = Number(bytes);
    if (!Number.isFinite(value) || value < 0) return '-';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

/** 任务状态 → i18n key；后端将来新增状态时降级为「生成中」而不是渲染出裸状态码 */
export const exportStatusKey = (status: string): string => {
    switch (status) {
        case 'PENDING':
            return 'orderExport.status.pending';
        case 'SUCCESS':
            return 'orderExport.status.completed';
        case 'FAILED':
            return 'orderExport.status.failed';
        case 'RUNNING':
        default:
            return 'orderExport.status.generating';
    }
};

/** 任务是否已落终态（决定弹窗停止轮询、记录页展示下载按钮） */
export const isTerminalStatus = (status: string): boolean => status === 'SUCCESS' || status === 'FAILED';
